import React, { useState } from 'react';
import { Field, Form, Formik, FormikProps } from 'formik';
import { Address, Details, DetailsPageData, Validate, EditData } from 'components/DetailsPageBoilerplate/types';
import { OldAddress } from 'domain/Address';
import FullAddress from 'components/Forms/Address/Templates/FullAddress';
import { telephoneMask } from 'shared/Inputs/inputMasks';
import validationSchema from 'components/DetailsPageBoilerplate/validationSchema';
import { BUTTON_MODS } from 'shared/Inputs/interface';
import { populateAddressHandler } from 'utils/address/form';
import FormInputNewUi from 'shared/Inputs/Inline/FormInputNewUi';
import FormSelectNewUi from 'shared/Inputs/Inline/FormSelectNewUi';

const formatAddressInline = (address: Address) =>
  [address.houseNumber, `${address.addressLine1},`, `${address.city},`, address.postcode.toUpperCase()].join(' ');

const formatStatus = (status: boolean) => (status ? 'Active' : 'Inactive');

const DetailsForm: React.FC<DetailsPageData> = ({ details, data = {}, onSubmit }: DetailsPageData) => {
  const [edit, setEdit] = useState<EditData>({
    address: false,
    contactNumber: false,
    email: false,
    submissionRoute: false,
  });

  const initialValues: any = { ...data };

  const statuses: any[] = [
    initialValues.isFCAActive !== undefined
      ? {
          value: formatStatus(initialValues.isFCAActive),
          name: 'isFCAActive',
          label: 'FCA Status',
        }
      : null,
    initialValues.isLMCActive !== undefined
      ? {
          value: formatStatus(initialValues.isLMCActive),
          name: 'isLMCActive',
          label: 'LMC Status',
        }
      : null,
  ].filter((details: Details | null) => details !== null);

  [...details, ...statuses].forEach((detail) => {
    initialValues[detail.name] = detail.value;
  });

  const validateSubmitEnabled = (isCorrect: boolean) => {
    return (isSaving: boolean) => (isSaving && isCorrect ? Promise.resolve() : Promise.reject());
  };

  const onContactNumberChange = async (setFieldValue: any, values: any, validate: Validate) => {
    const mode: boolean = await validate(edit.contactNumber)
      .then(() => {
        onSubmit({ phone: values.contactNumber });
        return !edit.contactNumber;
      })
      .catch(() => {
        return true;
      });

    setEdit((edit) => ({
      ...edit,
      contactNumber: mode,
    }));
  };

  const onAddressChange = async (setFieldValue: any, values: any, validate: Validate) => {
    const mode: boolean = await validate(edit.address)
      .then(() => {
        const address: OldAddress = values.address;
        onSubmit({
          post_code: address.postcode.toUpperCase(),
          house_number: address.houseNumber,
          street_line_1: address.addressLine1,
          street_line_2: address.addressLine2,
          county: address.county,
          city: address.city,
        });
        setFieldValue('fullAddress', formatAddressInline(values.address));
        return !edit.address;
      })
      .catch(() => {
        return true;
      });

    setEdit((edit: EditData) => ({
      ...edit,
      address: mode,
    }));
  };

  const onEmailChange = async (setFieldValue: any, values: any, validate: Validate) => {
    const mode: boolean = await validate(edit.email)
      .then(() => {
        onSubmit({ email: values.email });
        return !edit.email;
      })
      .catch(() => {
        return true;
      });

    setEdit((edit) => ({
      ...edit,
      email: mode,
    }));
  };

  const onSubmissionRouteChange = async (
    values: any,
    validate: Validate,
    setFieldError: (field: string, message: string) => void,
  ) => {
    const mode: boolean = await validate(edit.submissionRoute)
      .then(() => {
        onSubmit({ submission_route: values.submissionRoute.value });
        return !edit.submissionRoute;
      })
      .catch(() => {
        if (!values.submissionRoute.value) setFieldError('submissionRoute', 'Value is missing');
        return true;
      });

    setEdit((edit) => ({
      ...edit,
      submissionRoute: mode,
    }));
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={() => {}}
        enableReinitialize={true}
        validationSchema={validationSchema}>
        {({ values, setFieldValue, setFieldTouched, errors, touched, setFieldError }: FormikProps<any>) => {
          const isValid = (fieldName: string) => !!(errors && errors[fieldName] && touched && touched[fieldName]);
          const isCorrect = (fieldName: string) =>
            Boolean(!(errors && errors[fieldName]) && touched && touched[fieldName]);

          if (!touched.fullAddress) {
            setFieldTouched('fullAddress', true);
          }

          return (
            <Form className="form admin-details">
              {details.map((detail) => {
                if (detail.options) {
                  return (
                    <Field
                      key={detail.name}
                      name={detail.name}
                      inputWidth={8}
                      buttonWidth={4}
                      component={FormSelectNewUi}
                      label={detail.label}
                      readOnly={!edit[detail.name]}
                      mode={edit[detail.name] ? BUTTON_MODS.OK : BUTTON_MODS.CHANGE}
                      wrapperClass={'form__wrapper_newUi inline border bottom'}
                      options={detail.options}
                      buttonClass="button--newUi"
                      onClick={() => {
                        onSubmissionRouteChange(
                          values,
                          validateSubmitEnabled(!!values.submissionRoute.value && !!touched.submissionRoute),
                          setFieldError,
                        );
                      }}
                    />
                  );
                }

                return (
                  <Field
                    key={detail.name}
                    name={detail.name}
                    component={FormInputNewUi}
                    label={detail.label}
                    readOnly={true}
                    wrapperClass={'form__wrapper_newUi inline border bottom'}
                  />
                );
              })}

              {initialValues.address && (
                <Field
                  name="fullAddress"
                  component={FormInputNewUi}
                  label="Address"
                  readOnly={true}
                  error={errors && errors.address ? 'Please amend address' : ''}
                  inputWidth={8}
                  buttonWidth={4}
                  onChangeClick={() =>
                    onAddressChange(setFieldValue, values, validateSubmitEnabled(errors.address === undefined))
                  }
                  buttonClass="button--newUi"
                  mode={edit.address ? BUTTON_MODS.OK : BUTTON_MODS.CHANGE}
                  wrapperClass={'form__wrapper_newUi inline border bottom'}
                />
              )}

              {values.address && edit.address && (
                <FullAddress
                  wrapperClass="new-ui"
                  isValid={isValid}
                  isCorrect={isCorrect}
                  prefix="address"
                  requiredFields={{}}
                  onSelect={populateAddressHandler(setFieldValue)}
                  showFlatNumber={true}
                  useRebrand
                />
              )}

              {initialValues.email !== undefined && (
                <Field
                  name="email"
                  component={FormInputNewUi}
                  label="Email Address"
                  type="email"
                  inputWidth={8}
                  buttonWidth={4}
                  onChangeClick={() => {
                    onEmailChange(setFieldValue, values, validateSubmitEnabled(isCorrect('email')));
                  }}
                  mode={edit.email ? BUTTON_MODS.OK : BUTTON_MODS.CHANGE}
                  readOnly={!edit.email}
                  wrapperClass={'form__wrapper_newUi inline border bottom'}
                  buttonClass="button--newUi"
                />
              )}

              {initialValues.contactNumber !== undefined && (
                <Field
                  name="contactNumber"
                  component={FormInputNewUi}
                  label="Contact No"
                  mask={telephoneMask}
                  showMask={true}
                  inputWidth={8}
                  buttonWidth={4}
                  onChangeClick={() =>
                    onContactNumberChange(setFieldValue, values, validateSubmitEnabled(isCorrect('contactNumber')))
                  }
                  mode={edit.contactNumber ? BUTTON_MODS.OK : BUTTON_MODS.CHANGE}
                  readOnly={!edit.contactNumber}
                  wrapperClass={'form__wrapper_newUi inline border bottom'}
                  buttonClass="button--newUi"
                />
              )}

              {statuses.length > 0 && (
                <div className="details-row statuses">
                  {statuses.map((status) => (
                    <Field
                      key={`detail-${status.name}`}
                      name={status.name}
                      component={FormInputNewUi}
                      label={status.label}
                      inputWidth={8}
                      readOnly={true}
                    />
                  ))}
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default DetailsForm;
